import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchWithAuth } from '../../utils/authUtils';
import tokenStore from '../../utils/tokenStore';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DataContext } from '../../Provider/dataProvider';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { memberLevelLabel, normalizeTierLabel } from '../../constants/membershipPackages';
import { copyToClipboard } from '../../utils/copyToClipboard';
import '../../css/profile.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const Profile = () => {
    const { data } = useContext(DataContext);
    const { access } = useFeatureAccess();
    const navigate = useNavigate();
    const { accountId } = useParams();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [viewRole, setViewRole] = useState('user');
    const [formData, setFormData] = useState({
        fullName: '',
        gender: 'Nam',
        dateOfBirth: '',
        height: '',
        phone: '',
        address: '',
        linkKey: '',
    });
    const [avatarUrl, setAvatarUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const viewedAccountId = accountId ? Number(accountId) : Number(data?.id || 0);
    const isOwnProfile = Number(data?.id || 0) === viewedAccountId;
    const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '' });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState('');
    const [passwordMessageType, setPasswordMessageType] = useState('');

    const normalizeDob = (dob) => {
        if (!dob) return '';

        if (typeof dob === 'string') {
            const viMatch = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            if (viMatch) {
                const [, dd, mm, y] = viMatch;
                return `${y}-${mm}-${dd}`;
            }

            const ymdOnlyMatch = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (ymdOnlyMatch) return dob;

            if (dob.includes('T')) {
                const d = new Date(dob);
                if (!Number.isNaN(d.getTime())) {
                    const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
                    utc.setUTCDate(utc.getUTCDate() + 1);
                    const yyyy = utc.getUTCFullYear();
                    const mm = String(utc.getUTCMonth() + 1).padStart(2, '0');
                    const dd = String(utc.getUTCDate()).padStart(2, '0');
                    return `${yyyy}-${mm}-${dd}`;
                }
            }

            const isoYmd = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (isoYmd) return `${isoYmd[1]}-${isoYmd[2]}-${isoYmd[3]}`;
        }

        try {
            const d = new Date(dob);
            if (!Number.isNaN(d.getTime())) {
                const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
                utc.setUTCDate(utc.getUTCDate() + 1);
                const yyyy = utc.getUTCFullYear();
                const mm = String(utc.getUTCMonth() + 1).padStart(2, '0');
                const dd = String(utc.getUTCDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
            }
        } catch { }

        return '';
    };

    useEffect(() => {
        if (!data?.id) {
            navigate('/login');
            return;
        }

        if (!viewedAccountId) {
            return;
        }

        fetch(`${API_BASE}/accounts/${viewedAccountId}`, {
            headers: {
                'Authorization': `Bearer ${tokenStore.getToken()}`,
            },
        })
            .then((r) => r.json())
            .then((result) => {
                if (!result?.success && !result?.data) return;
                const u = result?.data;
                setViewRole(u?.role || 'user');
                setFormData({
                    fullName: u?.full_name || u?.fullName || '',
                    gender: u?.gender === 'male' || u?.gender === 'female' ? (u.gender === 'male' ? 'Nam' : 'Nữ') : (u?.gender || 'Nam'),
                    dateOfBirth: normalizeDob(u?.dob || u?.dateOfBirth || u?.date_of_birth),
                    height: u?.height ?? '',
                    phone: u?.phone || '',
                    address: u?.address || '',
                    linkKey: u?.link_key || u?.linkKey || '',
                });
                setAvatarUrl(u?.avatar ? `${API_BASE}${u.avatar}` : '');
            })
            .catch(() => {});
    }, [data, navigate, viewedAccountId]);

    const profileInitial = useMemo(() => {
        const sourceName = formData.fullName || data?.fullName || data?.username || '';
        return sourceName.trim() ? sourceName.trim().charAt(0).toUpperCase() : '?';
    }, [formData.fullName, data]);

    const rawRole = (viewRole || data?.role || 'user').toString().toLowerCase();
    const roleMap = { elderly: 'Người cao tuổi', relative: 'Người chăm sóc', admin: 'Quản trị viên', user: 'Người dùng' };
    const displayRole = roleMap[rawRole] || roleMap.user;
    const isElderly = rawRole === 'elderly';

    const copyLinkKey = async () => {
        if (!formData.linkKey) return;
        try {
            await copyToClipboard(formData.linkKey);
            setMessage('Đã sao chép mã kết nối');
            setMessageType('success');
        } catch (error) {
            setMessage('Không thể sao chép mã kết nối');
            setMessageType('error');
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((previous) => ({ ...previous, [name]: value }));
    };

    const handlePasswordChange = (event) => {
        const { name, value } = event.target;
        setPasswordData((previous) => ({ ...previous, [name]: value }));
    };

    const handleSubmitPassword = async (event) => {
        event.preventDefault();
        if (!passwordData.currentPassword || !passwordData.newPassword) {
            setPasswordMessage('Vui lòng nhập đầy đủ mật khẩu hiện tại và mật khẩu mới');
            setPasswordMessageType('error');
            return;
        }
        setPasswordLoading(true);
        setPasswordMessage('');
        try {
            const res = await fetchWithAuth(`${API_BASE}/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword,
                }),
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok || !payload?.success) {
                throw new Error(payload?.message || 'Đổi mật khẩu thất bại');
            }
            setPasswordMessage('Đổi mật khẩu thành công');
            setPasswordMessageType('success');
            setPasswordData({ currentPassword: '', newPassword: '' });
        } catch (err) {
            setPasswordMessage(err.message || 'Không thể đổi mật khẩu');
            setPasswordMessageType('error');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!isOwnProfile) {
            setMessage('Bạn chỉ có thể chỉnh sửa hồ sơ của chính mình');
            setMessageType('error');
            return;
        }

        setLoading(true);
        setMessage('');
        const genderApiMap = { Nam: 'male', 'Nữ': 'female', 'Khác': 'other' };
        const sendGender = genderApiMap[formData.gender] || formData.gender;

        const updateData = {
            fullName: formData.fullName,
            full_name: formData.fullName,
            gender: sendGender,
            dateOfBirth: formData.dateOfBirth,
            height: formData.height,
            phone: formData.phone,
            address: formData.address,
        };

        fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/accounts/${data?.id}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${tokenStore.getToken()}`,
            },
            body: JSON.stringify(updateData),
        })
            .then((response) => response.json())
            .then((result) => {
                if (!result.success) {
                    throw new Error(result.message || 'Lỗi cập nhật hồ sơ');
                }
                setMessage('Đã lưu thay đổi thành công');
                setMessageType('success');
                try { window.dispatchEvent(new Event('tamcare:profile-updated')); } catch (_) {}
            })
            .catch((err) => {
                setMessage(err.message || 'Không thể lưu thay đổi');
                setMessageType('error');
            })
            .finally(() => setLoading(false));
    };

    const onClickAvatar = () => {
        if (!isOwnProfile) return;
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const onAvatarSelected = async (e) => {
        if (!e.target.files || !e.target.files[0]) return;
        const file = e.target.files[0];
        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('image', file);
            const upRes = await fetchWithAuth(`${API_BASE}/api/upload/avatar`, {
                method: 'POST',
                body: formData,
            });
            const upJson = await upRes.json().catch(() => ({}));
            if (!upRes.ok || !upJson?.imageUrl) {
                throw new Error(upJson?.message || 'Tải ảnh thất bại');
            }

            const imagePath = upJson.imageUrl; // e.g. /document/avatar/avatar-xxx.png
            const patchRes = await fetchWithAuth(`${API_BASE}/accounts/${data?.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ avatar: imagePath }),
            });
            const patchJson = await patchRes.json().catch(() => ({}));
            if (!patchRes.ok || !patchJson?.success) {
                throw new Error(patchJson?.message || 'Cập nhật avatar thất bại');
            }
            setAvatarUrl(`${API_BASE}${imagePath}`);
            try { window.dispatchEvent(new Event('tamcare:profile-updated')); } catch (_) {}
            setMessage('Đã cập nhật ảnh đại diện');
            setMessageType('success');
        } catch (err) {
            setMessage(err?.message || 'Không thể cập nhật ảnh đại diện');
            setMessageType('error');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div className="tamcare-profile-page">
            <div className="tamcare-profile-shell">
                <Link to={isOwnProfile ? '/' : '/my-relatives'} className="tamcare-back-link">
                    <i className="fa-solid fa-arrow-left"></i> Quay lại trang chủ
                </Link>

                <div className="tamcare-profile-card">
                    <div className="tamcare-profile-card__header">
                        <div className="tamcare-profile-avatar" onClick={onClickAvatar} style={{ cursor: isOwnProfile ? 'pointer' : 'default', position: 'relative', overflow: 'hidden' }} title={isOwnProfile ? 'Nhấp để thay đổi ảnh đại diện' : undefined}>
                            {avatarUrl ? (
                                <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                            ) : (
                                profileInitial
                            )}
                            {isOwnProfile && uploading && (
                                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                                    <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }}></i> Đang tải...
                                </div>
                            )}
                        </div>
                        <input type="file" accept="image/*" ref={fileInputRef} onChange={onAvatarSelected} style={{ display: 'none' }} />
                        <div>
                            <h2>{formData.fullName || 'Thông tin cá nhân'}</h2>
                            <span className="tamcare-role-badge">{displayRole}</span>
                        </div>
                    </div>
                    



                    {message && (
                        <div className={`tamcare-alert ${messageType === 'success' ? 'tamcare-alert--success' : 'tamcare-alert--error'}`}>
                            {message}
                        </div>
                    )}

                    {isOwnProfile && (
                        <div className={`tamcare-membership-box ${access.isPremium ? 'tamcare-membership-box--active' : 'tamcare-membership-box--inactive'}`} style={{ marginBottom: 20 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <div className="tamcare-icon-circle tamcare-icon-circle--premium">
                                        <i className="fa-solid fa-gem" />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 800, color: '#1e293b' }}>
                                            TamCare {access.isPremium ? 'Pro' : 'Miễn phí'}
                                        </div>
                                        <div className="tamcare-subtext">
                                            {access.isPremium
                                                ? `Gói Pro đang hoạt động${access.premiumExpiry ? ` · Hết hạn ${new Date(access.premiumExpiry).toLocaleDateString('vi-VN')}` : ''}`
                                                : 'Blog, hồ sơ bệnh án và cửa hàng miễn phí · Nâng cấp Pro (119k/tháng) để mở toàn bộ tính năng'}
                                        </div>
                                    </div>
                                </div>
                                <Link to="/membership" className="tamcare-inline-link tamcare-inline-link--warn">
                                    {access.isPremium ? 'Quản lý gói →' : 'Nâng cấp ngay →'}
                                </Link>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="tamcare-profile-form">
                        <div className="tamcare-section-title">Thông tin cá nhân</div>

                        <div className="tamcare-info-row">
                            <label>Họ và tên</label>
                            <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} disabled={!isOwnProfile} />
                        </div>

                        <div className="tamcare-info-row">
                            <label>Giới tính</label>
                            <select name="gender" value={formData.gender} onChange={handleChange} disabled={!isOwnProfile}>
                                <option value="Nam">Nam</option>
                                <option value="Nữ">Nữ</option>
                                <option value="Khác">Khác</option>
                            </select>
                        </div>

                        <div className="tamcare-info-row">
                            <label>Ngày sinh</label>
                            {/* Custom hiển thị dd/MM/yyyy, nhưng vẫn lưu value theo format YYYY-MM-DD */}
                            <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth || ''}
                                onChange={(e) => {
                                    const raw = e.target.value; // YYYY-MM-DD
                                    if (!raw) {
                                        setFormData((prev) => ({ ...prev, dateOfBirth: '' }));
                                        return;
                                    }
                                    setFormData((prev) => ({ ...prev, dateOfBirth: raw }));
                                }}
                                disabled={!isOwnProfile}
                            />
                        </div>

                        <div className="tamcare-info-row">
                            <label>Chiều cao (cm)</label>
                            <input
                                type="number"
                                name="height"
                                value={formData.height}
                                onChange={handleChange}
                                disabled={!isOwnProfile}
                                min="0"
                                step="0.1"
                            />
                        </div>

                        <div className="tamcare-info-row">
                            <label>Số điện thoại</label>
                            <input type="text" name="phone" value={formData.phone} onChange={handleChange} disabled={!isOwnProfile} />
                        </div>

                        <div className="tamcare-info-row">
                            <label>Địa chỉ</label>
                            <input type="text" name="address" value={formData.address} onChange={handleChange} disabled={!isOwnProfile} placeholder="Số nhà, phường/xã, quận/huyện, tỉnh/thành" />
                        </div>

                        <div className="tamcare-info-row tamcare-info-row--last">
                            <label>Mã kết nối cá nhân</label>
                            <span className="tamcare-link-key">{formData.linkKey || 'CHƯA CÓ'}</span>
                            {formData.linkKey && isOwnProfile && (
                                <button type="button" className="tamcare-copy-btn" onClick={copyLinkKey} style={{ marginLeft: 12 }}>
                                    Sao chép
                                </button>
                            )}
                        </div>

                        {isOwnProfile && (
                            <button type="submit" className="tamcare-btn-save" disabled={loading}>
                                {loading ? 'Đang lưu...' : 'Lưu tất cả thay đổi'}
                            </button>
                        )}
                    </form>

                    {isOwnProfile && (
                        <form onSubmit={handleSubmitPassword} className="tamcare-profile-form" style={{ marginTop: 32 }}>
                            <div className="tamcare-section-title">Đổi mật khẩu</div>

                            {passwordMessage && (
                                <div className={`tamcare-alert ${passwordMessageType === 'success' ? 'tamcare-alert--success' : 'tamcare-alert--error'}`}>
                                    {passwordMessage}
                                </div>
                            )}

                            <div className="tamcare-info-row">
                                <label>Mật khẩu hiện tại</label>
                                <input
                                    type="password"
                                    name="currentPassword"
                                    value={passwordData.currentPassword}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>

                            <div className="tamcare-info-row">
                                <label>Mật khẩu mới</label>
                                <input
                                    type="password"
                                    name="newPassword"
                                    value={passwordData.newPassword}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>

                            <button type="submit" className="tamcare-btn-save" disabled={passwordLoading}>
                                {passwordLoading ? 'Đang đổi...' : 'Đổi mật khẩu'}
                            </button>
                        </form>
                    )}

                    {isElderly && isOwnProfile && (
                        <>
                            <div className="tamcare-section-title">Hồ sơ sức khỏe</div>

                            <Link to={viewedAccountId ? `/health/${viewedAccountId}` : '/health'} className="tamcare-info-box tamcare-health-box">
                                <div className="tamcare-info-main">
                                    <div className="tamcare-icon-circle tamcare-icon-circle--health">
                                        <i className="fa-solid fa-heart-pulse"></i>
                                    </div>
                                    <div>
                                        <small>Ghi chỉ số sức khỏe </small>
                                        <b>Chỉ số hôm nay (huyết áp, nhịp tim, cân nặng...)</b>
                                    </div>
                                </div>
                                <i className="fa-solid fa-chevron-right tamcare-chevron"></i>
                            </Link>

                            <Link to={viewedAccountId ? `/medical-history/${viewedAccountId}` : '/medical-history'} className="tamcare-info-box tamcare-medical-history-box">
                                <div className="tamcare-info-main">
                                    <div className="tamcare-icon-circle tamcare-icon-circle--gold">
                                        <i className="fa-solid fa-notes-medical"></i>
                                    </div>
                                    <div>
                                        <small>Hồ sơ khám bệnh </small>
                                        <b>Xem / cập nhật hồ sơ bệnh án</b>
                                    </div>
                                </div>
                                <i className="fa-solid fa-chevron-right tamcare-chevron"></i>
                            </Link>
                        </>
                    )}

                    {isOwnProfile && (
                        <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #fecaca' }}>
                            <div className="tamcare-section-title" style={{ color: '#b91c1c' }}>Xóa tài khoản</div>
                            <p style={{ color: '#64748b', marginBottom: 12 }}>
                                Xóa vĩnh viễn tài khoản và dữ liệu liên quan. Hành động này không thể hoàn tác.
                            </p>
                            <Link
                                to="/account/delete"
                                style={{
                                    display: 'inline-block',
                                    padding: '10px 18px',
                                    borderRadius: 8,
                                    background: '#fef2f2',
                                    color: '#b91c1c',
                                    border: '1px solid #fecaca',
                                    fontWeight: 700,
                                    textDecoration: 'none',
                                }}
                            >
                                Xóa tài khoản vĩnh viễn
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;
